if (!customElements.get('market-selector')) {
  class MarketSelector extends HTMLElement {
    constructor() {
      super();
      this.countryCodeInput = null;
      this.form = null;

      this.trigger = null;
      this.menu = null;

      this.boundOnTriggerClick = this.onTriggerClick.bind(this);
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);
      this.boundOnKeyDown = this.onKeyDown.bind(this);
      this.boundOnMenuClick = this.onMenuClick.bind(this);
    }

    connectedCallback() {
      this.countryCodeInput = this.querySelector('input[name="country_code"]');
      this.form = this.querySelector('form');

      this.trigger = this.querySelector('.market-selector-trigger');
      this.menu = this.querySelector('.market-selector-menu');

      if (this.trigger && this.menu) {
        this.trigger.addEventListener('click', this.boundOnTriggerClick);
        this.menu.addEventListener('click', this.boundOnMenuClick);
        document.addEventListener('click', this.boundOnDocumentClick);
        document.addEventListener('keydown', this.boundOnKeyDown);
      }

      this.normalizeUnsupportedMarketOnce();
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.boundOnTriggerClick);
      }
      if (this.menu) {
        this.menu.removeEventListener('click', this.boundOnMenuClick);
      }
      document.removeEventListener('click', this.boundOnDocumentClick);
      document.removeEventListener('keydown', this.boundOnKeyDown);
    }

    getCurrentIso() {
      return (this.dataset.currentIso || '').toUpperCase();
    }

    getAvailableIsos() {
      const raw = this.dataset.availableIsos || '';
      return raw
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
    }

    getFirstInternationalIso() {
      return (this.dataset.firstInternationalIso || '').toUpperCase();
    }

    isSupportedIso(iso) {
      if (!iso) return false;
      const available = this.getAvailableIsos();
      return available.includes(iso.toUpperCase());
    }

    getFallbackIso(preferredIso) {
      const preferred = (preferredIso || '').toUpperCase();
      if (preferred && this.isSupportedIso(preferred)) return preferred;

      const available = this.getAvailableIsos();
      if (available.length > 0) return available[0];

      return 'US';
    }

    normalizeUnsupportedMarketOnce() {
      try {
        const currentIso = this.getCurrentIso();
        const isUsOrCa = currentIso === 'US' || currentIso === 'CA';
        if (isUsOrCa) return;
        if (this.isSupportedIso(currentIso)) return;

        const key = 'market_selector_normalized';
        if (sessionStorage.getItem(key) === '1') return;
        sessionStorage.setItem(key, '1');

        // Prefer US if available; otherwise first available market.
        this.submitWithIso(this.getFallbackIso('US'));
      } catch (error) {
        // sessionStorage can throw in some privacy modes; ignore.
      }
    }

    onTriggerClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleMenu();
    }

    onDocumentClick(event) {
      if (!this.isMenuOpen()) return;
      if (event.target instanceof Node && this.contains(event.target)) return;
      this.closeMenu();
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      if (!this.isMenuOpen()) return;
      this.closeMenu();
      if (this.trigger) this.trigger.focus();
    }

    onMenuClick(event) {
      const target = event.target instanceof Element ? event.target.closest('[data-market]') : null;
      if (!target) return;

      const market = target.getAttribute('data-market');
      if (!market) return;

      event.preventDefault();
      event.stopPropagation();

      this.closeMenu();
      const iso = this.resolveIsoForMarket(market);
      this.submitWithIso(iso);
    }

    resolveIsoForMarket(market) {
      const normalized = (market || '').toLowerCase();
      if (normalized === 'us') return this.getFallbackIso('US');
      if (normalized === 'ca') return this.getFallbackIso('CA');

      const currentIso = this.getCurrentIso();
      const isUsOrCa = currentIso === 'US' || currentIso === 'CA';
      if (!isUsOrCa && this.isSupportedIso(currentIso)) return currentIso;

      const firstIntl = this.getFirstInternationalIso();
      if (firstIntl && this.isSupportedIso(firstIntl)) return firstIntl;

      return this.getFallbackIso('US');
    }

    isMenuOpen() {
      return this.menu && !this.menu.classList.contains('hidden');
    }

    openMenu() {
      if (!this.menu || !this.trigger) return;
      this.menu.classList.add('flex', 'flex-col');
      this.menu.classList.remove('hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    closeMenu() {
      if (!this.menu || !this.trigger) return;
      this.menu.classList.add('hidden');
      this.menu.classList.remove('flex', 'flex-col');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    toggleMenu() {
      if (this.isMenuOpen()) this.closeMenu();
      else this.openMenu();
    }

    submitWithIso(iso) {
      if (!this.form || !this.countryCodeInput) return;
      const safeIso = this.getFallbackIso(iso);
      this.countryCodeInput.value = safeIso;

      try {
        this.form.submit();
      } catch (error) {
        console.error('Market selection form submission failed:', error);
      }
    }
  }

  customElements.define('market-selector', MarketSelector);
}
